import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Info, Pencil, Trash } from 'lucide-react'

import styles from './TeamCard.module.scss'

import { isApiError } from '@/api'
import { type EnterpriseMemberRowResponse, TeamMemberRole, type TeamResponse } from '@/api/generated'
import { updateTeamMemberRole } from '@/api/team'
import { getTeamMembersTable } from '@/api/teamMembersTable'
import DataTable from '@/components/DataTable'
import FormErrorBanner from '@/components/FormErrorBanner'
import Tooltip from '@/components/Tooltip'
import { ROLE_LABELS } from '@/constants/enterprise'
import {
  isGeneralTeam,
  MY_ACTIVE_COST_CENTER_QUERY_KEY,
  TEAM_MEMBERS_PAGE_SIZE,
  TEAM_MEMBERS_TABLE_QUERY_KEY,
} from '@/pages/app/Team/Department/utils'
import { formatLastUsedAt } from '@/pages/app/UsageV2/TeamMembersTab/relativeDate'
import { fullName } from '@/pages/app/UsageV2/TeamMembersTab/TeamMembersTable'
import { selectCurrentEnterprise, selectCurrentUserId } from '@/store/features/user/userSlice'
import { type Role, SettingType } from '@/types'

interface TeamCardProps {
  team: TeamResponse
  onEdit: (team: TeamResponse) => void
  onDisband: (team: TeamResponse) => void
  onRemoveMember: (team: TeamResponse, member: EnterpriseMemberRowResponse) => void
}

const TeamCard = ({ team, onEdit, onDisband, onRemoveMember }: TeamCardProps) => {
  const isGeneral = isGeneralTeam(team)
  const callerUserId = useSelector(selectCurrentUserId) ?? ''
  const enterprise = useSelector(selectCurrentEnterprise)
  const showLastUsedAt = enterprise?.settings?.[SettingType.showLastUsedAt] === true
  const queryClient = useQueryClient()
  const [roleErrorByUser, setRoleErrorByUser] = useState<Record<string, string>>({})

  const teamMembersQueryKey = useMemo(
    () => [TEAM_MEMBERS_TABLE_QUERY_KEY, team.id, { sortBy: 'totalWords', sortDir: 'desc' }] as const,
    [team.id]
  )

  const { data, isLoading, error } = useQuery({
    queryKey: teamMembersQueryKey,
    queryFn: ({ signal }) =>
      getTeamMembersTable({
        teamId: team.id,
        limit: TEAM_MEMBERS_PAGE_SIZE,
        sortBy: 'totalWords',
        sortDir: 'desc',
        signal,
      }).then((r) => r.data),
  })

  const members = useMemo(() => data?.rows ?? [], [data])

  const roleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: TeamMemberRole }) => {
      const res = await updateTeamMemberRole(team.id, userId, { role })
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamMembersQueryKey })
      // Team-role change flips a member's is_effective_team_admin; refresh
      // the CC-wide snapshot so the next Create-Department open computes
      // sole-admin against the new roles.
      void queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
    },
    onError: (err: unknown, variables) => {
      const detail = isApiError(err) ? (err.detail ?? 'Failed to update role.') : 'Failed to update role.'
      setRoleErrorByUser((prev) => ({ ...prev, [variables.userId]: detail }))
    },
  })

  const columns = useMemo<ColumnDef<EnterpriseMemberRowResponse, unknown>[]>(() => {
    const cols: ColumnDef<EnterpriseMemberRowResponse, unknown>[] = [
      {
        id: 'member',
        header: 'Member',
        cell: ({ row }) => {
          const m = row.original
          const hasName = !!(m.firstName || m.lastName)
          return (
            <div className={styles.memberCell}>
              <span className={styles.memberName}>{fullName(m) ?? '—'}</span>
              {m.email && hasName && <span className={styles.memberEmail}>{m.email}</span>}
            </div>
          )
        },
      },
      {
        id: 'enterpriseRole',
        header: 'Enterprise role',
        cell: ({ row }) => (
          <span className={styles.roleBadge}>{ROLE_LABELS[(row.original.role ?? 'member') as Role]}</span>
        ),
      },
    ]
    if (!isGeneral) {
      cols.push({
        id: 'teamRole',
        header: 'Team role',
        cell: ({ row }) => {
          const m = row.original
          const isSelf = m.userId === callerUserId
          const teamRoleValue: TeamMemberRole = m.teamRole === 'admin' ? TeamMemberRole.Admin : TeamMemberRole.Member
          const pendingUserId = roleMutation.isPending ? roleMutation.variables?.userId : undefined
          const isUpdatingThisRow = pendingUserId === m.userId
          const rowError = roleErrorByUser[m.userId]
          return (
            <>
              {isSelf ? (
                <span className={styles.roleBadge}>{teamRoleValue === TeamMemberRole.Admin ? 'Admin' : 'Member'}</span>
              ) : (
                <select
                  className={styles.teamRoleSelect}
                  value={teamRoleValue}
                  onChange={(e) => {
                    setRoleErrorByUser((prev) => {
                      if (!(m.userId in prev)) return prev
                      const next = { ...prev }
                      delete next[m.userId]
                      return next
                    })
                    roleMutation.mutate({ userId: m.userId, role: e.target.value as TeamMemberRole })
                  }}
                  disabled={isUpdatingThisRow}
                  aria-label={`Team role for ${m.email ?? m.userId}`}
                >
                  <option value={TeamMemberRole.Admin}>Admin</option>
                  <option value={TeamMemberRole.Member}>Member</option>
                </select>
              )}
              {rowError && <p className={styles.rowError}>{rowError}</p>}
            </>
          )
        },
      })
    }
    // Gated per-enterprise via the showLastUsedAt gate (internal admin toggle).
    if (showLastUsedAt) {
      cols.push({
        id: 'lastUsed',
        header: 'Last used',
        cell: ({ row }) => <span className={styles.muted}>{formatLastUsedAt(row.original.lastUsedAt)}</span>,
      })
    }
    cols.push(
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span className={`${styles.statusBadge} ${styles[`status_${row.original.status}`] ?? ''}`}>
            {row.original.status}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className={styles.srOnly}>Actions</span>,
        cell: ({ row }) => (
          <button
            type="button"
            className={`${styles.iconButton} ${styles.destructive} ${styles.rowAction}`}
            onClick={() => onRemoveMember(team, row.original)}
            aria-label={`Remove ${row.original.email ?? row.original.userId} from ${team.name}`}
            title="Remove member"
          >
            <Trash size={14} aria-hidden />
          </button>
        ),
      }
    )
    return cols
  }, [isGeneral, callerUserId, roleMutation, roleErrorByUser, team, onRemoveMember, showLastUsedAt])

  return (
    <section className={styles.card} aria-label={`Department ${team.name}`}>
      <header className={styles.cardHeader}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{team.name}</span>
          {isGeneral && (
            <Tooltip text="Members not assigned to a specific department are listed here" placement="top" hideArrow>
              <Info size={14} className={styles.infoIcon} />
            </Tooltip>
          )}
          {!isGeneral && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onEdit(team)}
              aria-label={`Edit ${team.name}`}
            >
              <Pencil size={16} aria-hidden />
            </button>
          )}
        </div>
        <div className={styles.headerActions}>
          <span className={styles.memberCount}>
            {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
          </span>
          {!isGeneral && (
            <button
              type="button"
              className={`${styles.iconButton} ${styles.destructive}`}
              onClick={() => onDisband(team)}
              aria-label={`Delete ${team.name}`}
              title="Delete department"
            >
              <Trash size={16} aria-hidden />
            </button>
          )}
        </div>
      </header>

      <div className={styles.tableWrapper}>
        {error && <FormErrorBanner message="Failed to load members for this department." />}
        <DataTable
          data={members}
          columns={columns}
          isLoading={isLoading}
          loadingMessage="Loading members…"
          emptyMessage="No members yet."
          ariaLabel={`Members of ${team.name}`}
        />
      </div>
    </section>
  )
}

export default TeamCard
